import isEqual from "lodash/isEqual";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";

import BackEndService from "../api/BackEndService";
import ProductService from "../api/ProductService";
import { productManagementActions } from "../store/product-management-slice";
import { ProductUpdate } from "../types/product";

const getChangedFields = (
    original: ProductUpdate,
    current: ProductUpdate
): Partial<ProductUpdate> => {
    const changes: Partial<ProductUpdate> = {};

    Object.keys(current).forEach((key) => {
        const typedKey = key as keyof ProductUpdate;

        if (isEqual(original[typedKey], current[typedKey])) {
            return;
        }

        if (typedKey === "price") {
            changes.price = {
                price: current.price.price,
                purchasePrice: current.price.purchasePrice,
            };
            return;
        }
        (changes as any)[typedKey] = current[typedKey];

    });


    return changes;
};

export const useSaveProduct = () => {
    const dispatch = useDispatch();
    const { product, originalProduct, temporaryDocuments } = useSelector((state: any) => state.productManagement);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleFileUpload = async (document: any, productId: string) => {
        const file: File = document.file;

        try {
            const formData = new FormData();

            formData.append("uuid", uuidv4());
            formData.append("name", file.name);
            formData.append("file", file);

            const uploadedDocument: any = await BackEndService.UploadImage(formData);

            const documentPayload = {
                documentType: "datasheet",
                fileName: uploadedDocument.name,
                isInternal: false,
                media: uploadedDocument.uuid,
                attachedTo: {
                    type: "Product",
                    id: productId,
                },
            };

            const created: any = await BackEndService.AddDocument(documentPayload);

            dispatch(productManagementActions.documentUploaded({
                temporaryId: document.id,
                document: created.data,
            }));
        } catch (error) {
            setError(`Dokument konnte nicht hochgeladen werden "${file.name}"`);

            throw error;
        }
    };

    const uploadDocuments = (productId: string) =>
        Promise.all(
            (temporaryDocuments ?? []).map((document: any) =>
                handleFileUpload(document, productId)
            )
        );

    const saveProduct = async () => {
        setIsSaving(true);
        try {
            const response = await ProductService.CreateProduct({
                ...product,
                extendedPrices: product.extendedPrices.map(
                    (item: any) => ({
                        customerId: item.customerId,
                        price: item.price != null ? parseFloat(item.price.toString().replace(",", ".")) * 100 : null,
                    })
                ),
                price: {
                    price: product.price.price ? parseFloat((product.price.price).toString().replace(",", ".")) * 100 : 0,
                    purchasePrice: product.price.purchasePrice ? parseFloat((product.price.purchasePrice).toString().replace(",", ".")) * 100 : 0,
                },
            });

            const productId = response.data.id;

            // From here on the product exists: a retry after a failed upload has to update it,
            // not create it a second time.
            dispatch(productManagementActions.setEditProduct({ ...product, id: productId }));

            await uploadDocuments(productId);
        } catch (error: any) {

            setError(error.error ?? "Produkt konnte nicht gespeichert werden");
            throw error;
        }
        finally {
            setIsSaving(false);
        }
    };

    const updateProduct = async () => {
        if (!originalProduct) {
            return;
        }
        setIsSaving(true);
        try {
            const allChanges = getChangedFields(
                originalProduct,
                product
            );
            // SevDesk owns a SevDesk article's data; the backend only accepts its connected
            // materials.
            const changedFields: Partial<ProductUpdate> = product.isFromSevDesk
                ? (allChanges.relatedProducts ? { relatedProducts: allChanges.relatedProducts } : {})
                : allChanges;

            if (Object.keys(changedFields).length > 0) {
                const payload = {
                    ...changedFields,
                    ...(changedFields.extendedPrices && {
                        extendedPrices: changedFields.extendedPrices.map(
                            (item: any) => ({
                                customerId: item.customerId,
                                price: item.price != null ? parseFloat(item.price.toString().replace(",", ".")) * 100 : null,
                            })
                        ),
                    }),
                    ...(changedFields.price && {
                        price: {
                            price: changedFields.price.price ? parseFloat((changedFields.price.price).toString().replace(",", ".")) * 100 : 0,
                            purchasePrice: changedFields.price.purchasePrice ? parseFloat((changedFields.price.purchasePrice).toString().replace(",", ".")) * 100 : 0,

                        },
                    }),
                };

                await ProductService.UpdateProduct(
                    product.id,
                    payload
                );

                dispatch(productManagementActions.setEditProduct(product));
            }

            await uploadDocuments(product.id);
        } catch (error: any) {
            setError(error.error ?? "Produkt konnte nicht aktualisiert werden");
            throw error;
        } finally {
            setIsSaving(false);
        }
    }

    return { saveProduct, updateProduct, isSaving, error, setError }
};